import { create } from 'zustand';
import type { AudioItem, GameEventPublic, MyAnswer, RoomPublic } from '@listen-draw/shared';

export type ConnStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface AudioRequest {
  id: number;
  items: AudioItem[];
  rate: number;
  gapMs: number;
  replay: boolean;
}

export interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'ok' | 'bad';
}

export interface FatalError {
  code: string;
  message: string;
}

interface ClientStore {
  status: ConnStatus;
  role: 'host' | 'player' | null;
  playerId: string | null;
  token: string | null;
  state: RoomPublic | null;
  /** serverTime - clientTime（ms） */
  offset: number;
  rttMs: number | null;
  events: GameEventPublic[];
  toasts: Toast[];
  fatal: FatalError | null;
  audio: AudioRequest | null;
  /** 現在読み上げ中の選択肢（Host のみ更新） */
  audioProgress: number | null;
  /** 進行中のラウンドで自分が押した回答（participant のみ。roundIndex で新旧を判定する） */
  myAnswer: MyAnswer | null;

  setStatus(s: ConnStatus): void;
  setWelcome(
    role: 'host' | 'player',
    token: string,
    playerId: string | undefined,
    state: RoomPublic,
    myAnswer?: MyAnswer | null,
  ): void;
  setMyAnswer(a: MyAnswer | null): void;
  setState(state: RoomPublic): void;
  setClock(offset: number, rtt: number): void;
  pushEvent(e: GameEventPublic): void;
  toast(text: string, kind?: Toast['kind']): void;
  dismissToast(id: number): void;
  setFatal(f: FatalError | null): void;
  setAudio(a: AudioRequest | null): void;
  setAudioProgress(i: number | null): void;
  reset(): void;
}

let seq = 1;

export const useStore = create<ClientStore>((set) => ({
  status: 'idle',
  role: null,
  playerId: null,
  token: null,
  state: null,
  offset: 0,
  rttMs: null,
  events: [],
  toasts: [],
  fatal: null,
  audio: null,
  audioProgress: null,
  myAnswer: null,

  setStatus: (status) => set({ status }),
  setWelcome: (role, token, playerId, state, myAnswer) =>
    set({ role, token, playerId: playerId ?? null, state, fatal: null, myAnswer: myAnswer ?? null }),
  setMyAnswer: (myAnswer) => set({ myAnswer }),
  setState: (state) => set({ state }),
  setClock: (offset, rttMs) => set({ offset, rttMs }),
  pushEvent: (e) => set((s) => ({ events: [e, ...s.events].slice(0, 60) })),
  toast: (text, kind = 'info') =>
    set((s) => {
      const id = seq++;
      setTimeout(() => useStore.getState().dismissToast(id), 2600);
      return { toasts: [...s.toasts, { id, text, kind }].slice(-4) };
    }),
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  setFatal: (fatal) => set({ fatal }),
  setAudio: (audio) => set({ audio }),
  setAudioProgress: (audioProgress) => set({ audioProgress }),
  reset: () =>
    set({
      status: 'idle',
      role: null,
      playerId: null,
      token: null,
      state: null,
      events: [],
      fatal: null,
      audio: null,
      audioProgress: null,
      myAnswer: null,
    }),
}));

/** サーバー時刻（推定） */
export function serverNow(): number {
  return Date.now() + useStore.getState().offset;
}

export function me() {
  const { state, playerId } = useStore.getState();
  return state?.players.find((p) => p.id === playerId) ?? null;
}
